import React, { useState } from 'react';
import { Partner, UpdatePartnerRequest } from '../../types/partner';
import { partnersApi } from '../../services/api/partners';
import { Button } from '../shared/Button';

interface EditPartnerProps {
  partner: Partner;
  onClose: () => void;
  onUpdate: () => void;
}

function EditPartner({ partner, onClose, onUpdate }: EditPartnerProps) {
  const [name, setName] = useState(partner.name);
  const [description, setDescription] = useState(partner.description || '');
  const [isActive, setIsActive] = useState(partner.isActive);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  // Validate name on change
  const validateName = (value: string): string | null => {
    if (!value || !value.trim()) {
      return 'Partner name is required';
    }
    if (value.length < 3) {
      return 'Partner name must be at least 3 characters';
    }
    if (value.length > 50) {
      return 'Partner name must be at most 50 characters';
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(value)) {
      return 'Partner name can only contain letters, numbers, underscores, and hyphens';
    }
    return null;
  };

  const handleNameChange = (value: string) => {
    setName(value);
    setNameError(validateName(value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate before submission
    const nameValidationError = validateName(name);
    if (nameValidationError) {
      setNameError(nameValidationError);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updateData: UpdatePartnerRequest = {
        name: name.trim(),
        description: description.trim() || undefined,
        isActive
      };

      await partnersApi.updatePartner(partner.partnerId, updateData);
      // Success: close modal and trigger parent refresh
      onUpdate();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update partner';
      setError(errorMessage);
      // Do NOT close the modal or call onUpdate on failure.
      // The error is displayed inline in the form so the user can see it and retry.
    } finally {
      setSaving(false);
    }
  };

  const hasChanges =
    name !== partner.name ||
    description !== (partner.description || '') ||
    isActive !== partner.isActive;

  return (
    <div
      className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[1000] p-4"
      onClick={onClose}
    >
      <div
        className="
          bg-white dark:bg-neutral-900
          rounded-lg shadow-xl
          w-full max-w-md max-h-[90vh] overflow-y-auto
          border border-neutral-200 dark:border-neutral-700
        "
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="
          flex justify-between items-center
          px-5 py-4 border-b border-neutral-200 dark:border-neutral-700
        ">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
            Edit Partner
          </h2>
          <button
            className="
              w-8 h-8 flex items-center justify-center
              rounded text-2xl leading-none
              text-neutral-500 hover:text-neutral-900
              dark:text-neutral-400 dark:hover:text-neutral-100
              hover:bg-neutral-100 dark:hover:bg-neutral-800
              transition-colors duration-150
            "
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Partner Name */}
          <div className="space-y-1.5">
            <label
              htmlFor="partner-name"
              className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
            >
              Partner Name *
            </label>
            <input
              id="partner-name"
              type="text"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Enter partner name"
              disabled
              title="Partner name is immutable after creation — it determines the S3 bucket name"
              className={`
                w-full px-3 py-2.5
                bg-white dark:bg-neutral-800
                border rounded-md text-sm
                text-neutral-900 dark:text-neutral-100
                placeholder:text-neutral-400 dark:placeholder:text-neutral-500
                transition-all duration-200
                ${nameError
                  ? 'border-danger-500 focus:ring-danger-500 focus:border-danger-500'
                  : 'border-neutral-300 dark:border-neutral-600 focus:ring-primary-500 focus:border-primary-500'
                }
                focus:outline-none focus:ring-2 focus:ring-offset-0
                disabled:bg-neutral-100 dark:disabled:bg-neutral-850 disabled:cursor-not-allowed
              `}
            />
            {nameError && (
              <span className="block text-xs text-danger-600 dark:text-danger-400">
                {nameError}
              </span>
            )}
            <small className="block text-xs text-neutral-500 dark:text-neutral-400">
              Partner name cannot be changed. It determines the S3 bucket name.
            </small>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <label
              htmlFor="partner-description"
              className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
            >
              Description
            </label>
            <textarea
              id="partner-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter partner description (optional)"
              rows={3}
              maxLength={500}
              disabled={saving}
              className="
                w-full px-3 py-2.5
                bg-white dark:bg-neutral-800
                border border-neutral-300 dark:border-neutral-600
                rounded-md text-sm
                text-neutral-900 dark:text-neutral-100
                placeholder:text-neutral-400 dark:placeholder:text-neutral-500
                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                transition-all duration-200
                disabled:bg-neutral-100 dark:disabled:bg-neutral-850 disabled:cursor-not-allowed
                resize-none
              "
            />
            <small className="block text-xs text-neutral-500 dark:text-neutral-400">
              {description.length}/500 characters
            </small>
          </div>

          {/* Active Checkbox */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={saving}
                className="
                  w-4 h-4 rounded
                  border-neutral-300 dark:border-neutral-600
                  text-primary-600 focus:ring-primary-500
                  bg-white dark:bg-neutral-800
                  disabled:cursor-not-allowed
                "
              />
              <span className="text-sm text-neutral-700 dark:text-neutral-300">
                Partner is active
              </span>
            </label>
            <small className="block text-xs text-neutral-500 dark:text-neutral-400">
              Inactive partners will not process any transfers
            </small>
          </div>

          {/* Error Message */}
          {error && (
            <div className="
              p-3 rounded-md
              bg-danger-50 dark:bg-danger-900/30
              border border-danger-200 dark:border-danger-800
              text-sm text-danger-700 dark:text-danger-300
            ">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="
            flex justify-end gap-3 pt-4
            border-t border-neutral-200 dark:border-neutral-700
          ">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saving || !hasChanges || !!nameError}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditPartner;
